'use client';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { H2 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faMagnifyingGlassPlus } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlassPlus';
import Image from 'next/image';
import { CitationProps as Citation } from '@osmosis/shared/types/notes';
import { useUserContext } from '../../context/UserContext';
import { text4, text5 } from '@osmosis/tailwind-ui/styles/text';
import {
  BodyCtaArgs,
  TrialCtaArgs,
} from '../../lib/composer/schemas/answersValueProp';
import AnswerBodyCTA from './AnswerBodyCTA';

// Full-screen zoom/pan lightbox; loaded only when the image is opened.
const ImageViewer = dynamic(() => import('../ImageViewer'), { ssr: false });

type AnswerProps = {
  question: string;
  answer: string;
  blockContent: boolean;
};

/**
 * Component to render a single question and answer.
 */
const Answer = ({ question, answer, blockContent }: AnswerProps) => (
  <Box tabIndex={0} data-testid="question" className="mb-5">
    {/* h2 (not h3) so the page outline has no skipped level under the h1.
        No text-size override: questions use H2's default scale. */}
    <H2 className={`mb-4 font-bold ${text4}`}>{question}</H2>
    <div
      dangerouslySetInnerHTML={{ __html: answer }}
      className={`space-y-4 ${text4} [&_a]:text-blue-600${
        blockContent ? ' select-none' : ''
      }`}
      data-testid="question-answer-content"
    />
  </Box>
);

type AnswersObjectProps = {
  answersObj: {
    heading: string;
    subheading: string;
    image?: string;
    imageAltText?: string;
    citations?: Citation[];
    answers: QAProps[];
    modified?: string;
  };
  /** Editable copy for the in-article CTA (logged-out only). */
  bodyCta?: BodyCtaArgs;
  /** Shared button/reassurance copy for all Answer-page trial CTAs. */
  trialCta?: TrialCtaArgs;
};

type QAProps = {
  question: string;
  answer: string;
};

/**
 * Questions and answers component for the answers page.
 * Renders the list of questions and answers and the page's main image.
 * The heading, subheading and citations are rendered in AnswerHeader.
 */
const Answers = (props: AnswersObjectProps) => {
  const { heading, image, imageAltText, answers } = props.answersObj;
  const { bodyCta, trialCta } = props;

  const { user } = useUserContext();
  const intl = useIntl();

  const blockContent = !user;

  const [isImageZoomed, setIsImageZoomed] = useState(false);

  const altText = imageAltText
    ? imageAltText.trim()
    : `An infographic detailing the causes, signs and symptoms, diagnosis, and treatment of ${heading}`;

  const zoomLabel = intl.formatMessage(
    {
      id: 'answers.image.zoomLabel',
      defaultMessage: 'Zoom image: {alt}',
    },
    { alt: altText },
  );

  return (
    <Box
      className={`mx-auto rounded-lg border border-gray-300 px-4 py-5 font-sans ${text4} md:px-10`}
      tabIndex={0}
      data-testid="answers-container"
    >
      {answers && answers.length > 0 && (
        <>
          <Answer
            question={answers[0].question}
            answer={answers[0].answer}
            blockContent={blockContent}
          />

          {image ? (
            <Box className="relative mx-auto my-5 w-fit max-w-md">
              <button
                type="button"
                onClick={() => setIsImageZoomed(true)}
                className="block w-full cursor-zoom-in"
                aria-label={zoomLabel}
                data-testid="answer-page-image-zoom-trigger"
              >
                <Image
                  height={800}
                  width={800}
                  alt={altText}
                  src={image}
                  className={`w-full`}
                  data-testid="answer-page-image"
                />
                <Box
                  className={`absolute bottom-2 right-2 ${text5}`}
                  aria-hidden="true"
                >
                  <Icon icon={faMagnifyingGlassPlus} size={32} />
                </Box>
              </button>
            </Box>
          ) : null}

          {image && isImageZoomed ? (
            <ImageViewer
              src={image}
              title={altText}
              onClose={() => setIsImageZoomed(false)}
            />
          ) : null}

          {blockContent ? (
            <AnswerBodyCTA cta={bodyCta} trialCta={trialCta ?? {}} />
          ) : null}

          {answers.slice(1).map((qa: QAProps) => (
            <Answer
              key={`${qa.question}-${qa.answer}`}
              question={qa.question}
              answer={qa.answer}
              blockContent={blockContent}
            />
          ))}
        </>
      )}
    </Box>
  );
};

export default Answers;
